import React, { useState } from "react";
import SEO from "../components/SEO";

const macVersion = "0.3.5";
const winVersion = "0.3.5";
const macDownloadUrl = `/files/PWC_SKIN_II_${macVersion}_MacOS.zip`;
const winDownloadUrl = `/files/PWC_SKIN_II_${winVersion}_Windows.zip`;

const PWCskinV2: React.FC = () => {
  const [activeImage, setActiveImage] = useState<string | null>(null);

  return (
    <div className="bg-gray-900 text-white w-full overflow-x-hidden">
      <SEO
        title="PWC Skin II - Next-Gen Neural Amp Modeler Experience"
        description="A photorealistic visual overhaul for NAM with full A2 support, dual NAM loader, 5-Band EQ, and powerful FX section."
        image="https://www.pwc-profiles.com/images/plugins/pwc_nam_skin_iic/distance_shot2.jpg"
        url="https://www.pwc-profiles.com/plugins/pwcskinv2"
      />
      {/* Hero Section */}
      <section className="relative w-full bg-cover bg-center flex justify-center pt-4">
        {/* Background glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="container mx-auto text-center px-6 relative z-10 flex flex-col items-center mt-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-indigo-500/20 border border-cyan-400/40 shadow-[0_0_20px_rgba(34,211,238,0.25)] mb-5 backdrop-blur-md">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-400"></span>
            </span>
            <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-gradient-to-r from-cyan-400 to-blue-500 text-gray-950 rounded font-sans shadow-sm">
              NEW RELEASE
            </span>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-200">
              Next-Gen NAM Experience
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight max-w-4xl leading-tight flex items-center justify-center flex-wrap gap-3">
            <span>PWC Skin II</span>
            <span className="gradient-text">- Neural Amp Modeler</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-400 max-w-2xl font-light my-8">
            A photorealistic visual overhaul for NAM. Full A2 support. Dual NAM
            loader. 5-Band EQ. Powerful FX section.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#download"
              className=" px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold rounded-lg shadow-lg hover:shadow-blue-500/20 transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Download Free
            </a>
            <a
              href="#features"
              className="px-8 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-lg border border-white/10 hover:border-white/20 transition-all duration-200"
            >
              Explore Features
            </a>
          </div>

          <img
            className="relative z-20 -mb-8 md:-mb-12 mt-16 w-full max-w-[800px]"
            src="/images/plugins/pwc_nam_skin_iic/macbook_iic.webp"
            alt="PWCskinV2 overview image"
          />
        </div>
      </section>

      {/* Intro section */}
      <section className="pt-28 md:pt-36 lg:pt-44 bg-gray-100">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            Same Soul, Sharper Workflow, Better Visuals
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed pb-4  ">
            Inspired by an '80s gain monster, the UI is designed to let you
            focus on tone, not wrestling with your plugin. Everything you need
            is right where you expect it, with clear signals and controls that
            feel tactile and responsive.
          </p>
        </div>
      </section>

      <div
        className="w-full bg-cover bg-center min-h-48 bg-gray-100 pb-10"
        style={{
          backgroundImage:
            "url('/images/plugins/pwc_nam_skin_iic/iso_view_big_transparent.webp')",
        }}
      >
        <img
          className="w-full opacity-0"
          src="/images/plugins/pwc_nam_skin_iic/iso_view_big_transparent.webp"
          alt="PWCskinV2 iso view"
        />
      </div>

      {/* Core Features Overview */}
      <section id="features" className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Powerful Signal Processing Blocks
            </h2>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
              More than a visual overhaul—we integrated professional audio
              blocks to complete your signal chain inside a single plugin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Card 1: Core NAM */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">
                Neural Amp Modeler Core
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Run state-of-the-art machine-learning guitar and bass amp models
                (.nam files) with full profile metadata displays and
                comprehensive A1/A2 support.
              </p>
            </div>

            {/* Card 2: Tone Control & EQ */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-indigo-500/10 rounded-xl text-indigo-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">
                Tone Stack & Graphic EQ
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Flexible 3-band ToneStack (routable Pre or Post Gain Stage) with
                an intelligent "Bite switch" and a post-amp 5-band Graphic EQ..
              </p>
            </div>

            {/* Card 3: Noise Gate & IR */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-purple-500/10 rounded-xl text-purple-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Noise Gate & Tuner</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Eliminate high-gain hiss instantly. A fast, highly accurate
                tuner keeps your instrument perfectly calibrated.
              </p>
            </div>

            {/* Card 4: Binaural 3D */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-cyan-500/10 rounded-xl text-cyan-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071a9 9 0 0112.138 0M3.515 9.515a13.5 13.5 0 0116.97 0"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">
                3D Headphone Spatializer
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Premium HRTF engine that emulates natural room acoustics when
                using headphones.
              </p>
            </div>

            {/* Card 5: Dual FX Engine */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-pink-500/10 rounded-xl text-pink-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Dual Effects Engine</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Stereo delay with feedback/mix and a multi-mode reverb featuring
                Room, Plate, Hall, and custom Convolution environments.
              </p>
            </div>

            {/* Card 6: NAM Loader */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-teal-500/10 rounded-xl text-teal-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">2nd NAM Loader</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Load a second NAM file as a overdrive/boost pedal before the
                amp, or as a post-amp effect or cab.
              </p>
            </div>

            {/* Card 7: Input Calibration */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-amber-500/10 rounded-xl text-amber-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Input Calibration</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Manual and automatic input level alignment to precisely match
                model profiles and ensure optimal gain staging. Includes
                automatic calibration for the secondary pedal loader..
              </p>
            </div>

            {/* Card 8: CPU & Level Meters */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300 flex flex-col h-full group">
              <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-400 w-fit mb-4 group-hover:scale-110 transition-transform">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Real-time Telemetry</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Responsive input/output peak meters and a real-time CPU engine
                load display for seamless studio integration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mid Banner Shot */}
      <section
        className="w-full bg-cover bg-center h-[500px] relative overflow-hidden flex items-center justify-end px-6 md:px-24"
        style={{
          backgroundImage:
            "url('/images/plugins/pwc_nam_skin_iic/distance_shot2.webp')",
        }}
      >
        <div className="relative z-10 max-w-md p-8">
          <h3 className="text-2xl font-bold mb-4 gradient-text">
            Built on NAM
          </h3>
          <p className="text-gray-300 leading-relaxed text-sm">
            Powered by the high-performance NAM Core, ensuring sample-accurate
            processing, top-tier audio quality, and native platform resampling.
          </p>
        </div>
      </section>

      {/* Gear Icons Section */}
      <section className="py-20 bg-gray-900/20 relative overflow-hidden">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h3 className="text-2xl font-bold mb-2">
            Intuitive Gear Type Indicators
          </h3>
          <p className="text-gray-400 mb-10 max-w-md mx-auto text-sm">
            Instantly identify if the loaded profile is an amp, preamp, pedal,
            full rig, or studio rack gear.
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-6 justify-items-center">
            {[
              { name: "Amp", file: "icon_amp.svg" },
              { name: "Preamp", file: "icon_preamp.svg" },
              { name: "Pedal", file: "icon_pedal.svg" },
              { name: "Amp Cab", file: "icon_amp_cab.svg" },
              { name: "Studio", file: "icon_studio.svg" },
              { name: "Amp Pedal", file: "icon_amp_pedal.svg" },
            ].map((icon, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2 group">
                <div className="h-20 w-20 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 group-hover:border-blue-500/40 group-hover:bg-white/10 transition-all p-3">
                  <img
                    src={`/images/plugins/pwc_nam_skin/${icon.file}`}
                    alt={`${icon.name} Icon`}
                    className="h-full object-contain filter brightness-90 hover:brightness-100 transition-all"
                  />
                </div>
                <span className="text-xs text-gray-400 group-hover:text-gray-200 transition-colors font-medium">
                  {icon.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video & Media Showcase 
      <section className="py-20 bg-gray-950 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl flex flex-col items-center gap-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">See It In Action</h2>
            <p className="text-gray-400 max-w-md mx-auto">
              Check out the user interface and features in this quick
              walkthrough.
            </p>
          </div>

          <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black/60 p-2">
            <iframe
              width="100%"
              height="auto"
              style={{ aspectRatio: "16 / 9" }}
              src="https://www.youtube.com/embed/WtDBrt_0TG8?si=HWRO3eOZSFqiSemX"
              title="PWCskinV2 video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="rounded-lg"
            ></iframe>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full mt-4">
            <div className="rounded-xl overflow-hidden border border-white/10 group hover:border-blue-500/30 transition-colors">
              <img
                src="/images/plugins/pwc_nam_skin/pwc_nam_skin_screenshot_pedal.webp"
                alt="PWCskinV2 screenshot pedal view"
                className="w-full h-auto transform group-hover:scale-102 transition-transform duration-500"
              />
            </div>
            <div className="rounded-xl overflow-hidden border border-white/10 group hover:border-blue-500/30 transition-colors">
              <img
                src="/images/plugins/pwc_nam_skin/pwc_nam_skin_screenshot_back.webp"
                alt="PWCskinV2 screenshot options view"
                className="w-full h-auto transform group-hover:scale-102 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>*/}

      {/* Interface Gallery Section */}
      <section className="py-16 bg-gray-900/50 border-t border-white/5 relative overflow-hidden">
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold tracking-tight">
              Interface Gallery
            </h2>
            <p className="mt-3 text-gray-400 max-w-xl mx-auto text-sm">
              Take a closer look at the different views and built-in features of
              PWC Skin II.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                src: "/images/plugins/pwc_nam_skin_iic/nam_win.jpg",
                alt: "NAM main interface view",
                title: "Main View",
              },
              {
                src: "/images/plugins/pwc_nam_skin_iic/nam_pedals_win.jpg",
                alt: "NAM pedalboard routing view",
                title: "Pedalboard View",
              },
              {
                src: "/images/plugins/pwc_nam_skin_iic/nam_spatializer_win.jpg",
                alt: "NAM headphone spatializer view",
                title: "Spatializer",
              },
              {
                src: "/images/plugins/pwc_nam_skin_iic/nam_tuner_win.jpg",
                alt: "NAM built-in tuner view",
                title: "Built-in Tuner",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="group relative bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300 flex flex-col md:cursor-zoom-in"
                onClick={() => {
                  if (window.innerWidth >= 768) {
                    setActiveImage(item.src);
                  }
                }}
              >
                <div className="aspect-video overflow-hidden bg-black/40">
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 border-t border-white/5 bg-gray-950/40">
                  <h4 className="text-sm font-semibold text-gray-200 group-hover:text-blue-400 transition-colors">
                    {item.title}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section
        id="download"
        className="py-24 bg-gradient-to-b from-gray-900 to-gray-950 border-t border-white/5 relative overflow-hidden"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="container mx-auto px-6 max-w-4xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Download PWC Skin II
            </h2>
            <p className="mt-4 text-gray-400 max-w-xl mx-auto">
              Get the latest beta builds for macOS and Windows. Experience
              Neural Amp Modeling at its best.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* macOS Box */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 hover:bg-white/10 transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <svg
                    className="w-8 h-8 text-gray-200"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12.2 11.4c-.1-1.4 1.1-2.2 1.2-2.3-.7-1-1.7-1.1-2.1-1.2-1-.1-1.9.6-2.4.6-.5 0-1.2-.6-2-.6-1.1 0-2.1.6-2.7 1.6-1.2 2-1.2 5.1 0 7.2.6 1 1.3 2.1 2.4 2.1 1 0 1.4-.6 2.6-.6 1.2 0 1.5.6 2.6.6 1.1 0 1.7-1 2.3-2 .7-1.1 1-2.1 1-2.2-.1 0-2-1-2-3.1zM11.9 6.2c.4-.6.8-1.4.6-2.2-.7.1-1.6.5-2.1 1.2-.4.5-.8 1.3-.7 2.1.8.1 1.7-.5 2.2-1.1z" />
                  </svg>
                  <div>
                    <h3 className="text-xl font-bold">macOS Release</h3>
                    <span className="text-xs text-blue-400 font-mono">
                      {macVersion}
                    </span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-6">
                  Includes native binaries compatible with Intel and Apple
                  Silicon (M1/M2/M3) processors.
                </p>
                <div className="mb-8 bg-white/5 border border-white/5 rounded-xl p-4">
                  <span className="text-xs text-gray-500 uppercase tracking-wider block mb-2 font-semibold">
                    Included formats:
                  </span>
                  <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
                    <li>Standalone App</li>
                    <li>Audio Unit (AU) Plugin</li>
                    <li>VST3 Plugin</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <a
                  href={macDownloadUrl}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl hover:-translate-y-0.5 transition-all text-sm font-semibold group shadow-lg hover:shadow-blue-500/20"
                >
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download macOS Package (.zip)
                </a>
              </div>
            </div>

            {/* Windows Box */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 hover:bg-white/10 transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <svg
                    className="w-8 h-8 text-gray-200"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M0 3.449L9.75 2.1v9.45H0V3.449zM0 12.45h9.75v9.45L0 20.551v-8.102zM10.8 1.95L24 0v11.55H10.8V1.95zM10.8 12.45H24v11.55l-13.2-1.95v-9.6z" />
                  </svg>
                  <div>
                    <h3 className="text-xl font-bold">Windows Release</h3>
                    <span className="text-xs text-blue-400 font-mono">
                      {winVersion}
                    </span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-6">
                  Designed for Windows 10/11 64-bit systems with low-latency
                  ASIO audio support.
                </p>
                <div className="mb-8 bg-white/5 border border-white/5 rounded-xl p-4">
                  <span className="text-xs text-gray-500 uppercase tracking-wider block mb-2 font-semibold">
                    Included formats:
                  </span>
                  <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
                    <li>Standalone App</li>
                    <li>VST3 Plugin</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <a
                  href={winDownloadUrl}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl hover:-translate-y-0.5 transition-all text-sm font-semibold group shadow-lg hover:shadow-blue-500/20"
                >
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Windows Package (.zip)
                </a>
              </div>
            </div>
          </div>

          {/* Gumroad Support
          <div className="mt-16 p-8 text-center flex flex-col items-center">
            <h3 className="text-xl font-bold mb-4">Support the Project</h3>
            <p className="text-gray-400 text-sm max-w-md mb-8">
              PWC Skin II is free to download, but you can choose to purchase
              the full release to support future developments.
            </p>
            <div className="w-full max-w-sm">
              <ProfileCard profile={getProfileById("pwc-nam-skin")} />
            </div>
            <p className="mt-6 text-xs text-gray-500">
              Looking for the original release?{" "}
              <Link
                className="text-blue-400 hover:text-blue-300 underline"
                to="/plugins/pwcskinv1"
              >
                Visit PWCskinV1
              </Link>
            </p>
          </div> */}
        </div>
      </section>

      {/* What's New Section */}
      <section className="py-20 bg-gray-950 border-t border-white/5 relative overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="container mx-auto px-6 max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold tracking-tight">
              What's New
            </h2>
            <p className="mt-3 text-gray-400 max-w-xl mx-auto text-sm">
              Keep up with the latest updates, features, and optimizations.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 hover:border-blue-500/20 transition-all duration-300">
              {/* Version 0.3.5 Change Log */}
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/5 pb-4 mb-6">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs font-mono font-semibold">
                    v0.3.5
                  </span>
                  <span className="text-xs text-gray-500 font-medium">
                    Latest Release
                  </span>
                </div>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Optimized reverb mix volumes.
                  </span>
                </li>
              </ul>

              {/* Version 0.3.1 Change Log */}

              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/5 pb-4 mb-6  mt-12">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs font-mono font-semibold">
                    v0.3.1
                  </span>
                </div>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Added update checker to notify users of new releases.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Fixed various smaller bugs
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    adjusted version numbering
                  </span>
                </li>
              </ul>

              {/* Version 0.1.5 Change Log */}

              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/5 pb-4 mb-6 mt-12">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs font-mono font-semibold">
                    v0.1.5
                  </span>
                </div>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Optimized standalone input selector to support mono
                    operation, enabling selection of Input 2 as a source.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Added support for ultra-low latency 32-sample buffer size
                    settings.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Overhauled environments section.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Significantly optimized the 3D Headphone Spatializer engine,
                    cutting CPU utilization by 50% without compromising audio
                    fidelity.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Introduced configurable baking quality modes for binaural
                    reflections (Low for real-time performance, Ultra for 5x
                    spatial fidelity).
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Enhanced physical modeling on the Plate Reverb algorithm for
                    richer, more realistic decay.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
                  <span className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Expanded distribution formats with VST3 and AU support for
                    macOS, and VST3 alongside Standalone (.exe) formats for
                    Windows.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox / Image Zoom Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-md cursor-zoom-out p-4 md:p-8 overflow-hidden select-none"
          onClick={() => setActiveImage(null)}
        >
          <button
            className="fixed top-4 right-4 z-[60] text-white/80 hover:text-white bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-all text-sm font-semibold cursor-pointer shadow-lg backdrop-blur-sm"
            onClick={() => setActiveImage(null)}
          >
            Close &times;
          </button>
          <div className="relative max-w-full max-h-[75vh] md:max-h-[85vh] flex items-center justify-center">
            <img
              src={activeImage}
              alt="Enlarged screenshot"
              className="max-w-full max-h-[75vh] md:max-h-[85vh] object-contain rounded-lg shadow-2xl transition-all duration-300 scale-100"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PWCskinV2;
